import { labelForMetric } from '@chiroup/core/constants/metricCategories';
import { Maximize2 } from 'lucide-react';
import React from 'react';
import Sparkline from './charts/Sparkline';
import { computeTrend, formatTrend, trendArrow, trendColor } from './utils/trend';

type Props = {
  metricKey: string;
  value: number;
  previousValue: number;
  series?: number[];
  onClick?: () => void;
  onExpand?: () => void;
};

const INVERSE_METRICS = new Set([
  'npi_rejections',
  'enrollment_rejections',
  'format_837_rejections',
  'provider_loop_errors',
  'referring_provider_errors',
  'no_show_cancel_fees_added',
  'users_removed',
]);

const MetricCard: React.FC<Props> = ({
  metricKey,
  value,
  previousValue,
  series,
  onClick,
  onExpand,
}) => {
  const trend = computeTrend(value, previousValue);
  const isInverse = INVERSE_METRICS.has(metricKey);

  return (
    <div className="group relative flex flex-col items-stretch text-left rounded-lg border border-gray-200 bg-white p-3 hover:border-primary-300 hover:shadow-sm transition">
      {onExpand && (
        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            onExpand();
          }}
          className="absolute top-2 right-2 p-1 rounded text-gray-300 hover:text-gray-600 hover:bg-gray-100 transition"
          title="Expand chart"
        >
          <Maximize2 className="h-3.5 w-3.5" />
        </button>
      )}
      <button
        type="button"
        onClick={onClick}
        className="flex flex-col items-stretch text-left focus:outline-none"
      >
        <div className="flex items-baseline justify-between">
          <span className="text-2xl font-semibold text-gray-900 tabular-nums">
            {value.toLocaleString()}
          </span>
          <span className={`text-xs ${trendColor(trend.direction, isInverse)}`}>
            {trendArrow(trend.direction)} {formatTrend(trend)}
          </span>
        </div>
        <div className="text-xs text-gray-600 mt-1 line-clamp-2 min-h-[2rem]">
          {labelForMetric(metricKey)}
        </div>
        <div className="mt-2 -mx-1">
          {series && series.length > 0 ? (
            <Sparkline values={series} width={140} height={24} />
          ) : (
            <div className="h-6" />
          )}
        </div>
      </button>
    </div>
  );
};

export default MetricCard;
